import { useState, useMemo, useContext } from 'react';
import { useCommunitiesQuery, useProgramsQuery, CreatorDiscoveryEmailResource } from '@frontend/app/hooks';
import { useProspectsAddToCommunity } from '@frontend/app/hooks/useProspectsAddToCommunity';
import { useProspectsAddToProgram } from '@frontend/app/hooks/useProspectsAddToProgram';
import { useProspectsInviteToProgram } from '@frontend/app/hooks/useProspectsInviteToProgram';
import { GetAllPrograms_programs } from '@frontend/app/queries/types/GetAllPrograms';
import { GetCommunitiesQuery_communities } from '@frontend/app/queries/types/GetCommunitiesQuery';
import { IMessagingContext, MessagingContext } from '@frontend/context/MessagingContext';
import { InstagramCreator } from '@frontend/gql/social/graphql';
import { transformCreatorsToProspects } from '../components/table/utils/transform-creators-to-prospects';

const MUTATION_SOURCE = 'instagram_creator_overview';

interface UseCreatorOverviewActionsProps {
  creator: InstagramCreator | null;
  emailResource?: CreatorDiscoveryEmailResource;
  igdmActive: boolean;
}

export const useCreatorOverviewActions = ({
  creator,
  emailResource,
  igdmActive,
}: UseCreatorOverviewActionsProps) => {
  const messagingContext = useContext<IMessagingContext | undefined>(MessagingContext);

  // Search terms for filtering
  const [campaignSearchTerm, setCampaignSearchTerm] = useState('');
  const [groupSearchTerm, setGroupSearchTerm] = useState('');

  // Confirmation dialog states
  const [isInviteToCampaignConfirmOpen, setIsInviteToCampaignConfirmOpen] = useState(false);
  const [pendingInviteToCampaign, setPendingInviteToCampaign] = useState<GetAllPrograms_programs | null>(null);
  const [isAddToGroupConfirmOpen, setIsAddToGroupConfirmOpen] = useState(false);
  const [pendingAddToGroup, setPendingAddToGroup] = useState<GetCommunitiesQuery_communities | null>(null);
  const [isAddToCampaignConfirmOpen, setIsAddToCampaignConfirmOpen] = useState(false);
  const [pendingAddToCampaign, setPendingAddToCampaign] = useState<GetAllPrograms_programs | null>(null);

  // Data queries
  const { data: campaignsData } = useProgramsQuery();
  const { data: groupsData } = useCommunitiesQuery();

  // Filtered data
  const filteredCampaigns = useMemo(() => {
    if (!campaignsData?.programs) return [];
    const searchTerm = campaignSearchTerm.trim().toLowerCase();
    if (!searchTerm) return campaignsData.programs;
    return campaignsData.programs.filter((program) => program.title.toLowerCase().includes(searchTerm));
  }, [campaignsData, campaignSearchTerm]);

  const filteredGroups = useMemo(() => {
    if (!groupsData?.communities) return [];
    const searchTerm = groupSearchTerm.trim().toLowerCase();
    if (!searchTerm) return groupsData.communities;
    return groupsData.communities.filter((group) => group.title.toLowerCase().includes(searchTerm));
  }, [groupsData, groupSearchTerm]);

  // Check if creator can be invited
  const canBeInvited = useMemo(() => {
    if (!creator) return false;
    const hasEmail = creator.email && creator.email.trim() !== '';
    const inCreatorMarketplace = creator.inCreatorMarketplace === true;

    if (igdmActive) {
      return hasEmail || inCreatorMarketplace;
    } else {
      return hasEmail;
    }
  }, [creator, igdmActive]);

  // Mutations
  const [inviteToProgram, { loading: inviteLoading }] = useProspectsInviteToProgram({
    onCompleted: () => {
      messagingContext?.showSuccessMessage(`Successfully invited @${creator?.username} to the campaign!`);
      setIsInviteToCampaignConfirmOpen(false);
    },
    onError: (error) => {
      console.error('Failed to invite to campaign:', error);
      if (error.message.includes('Please, connect at least one email account to send invites')) {
        messagingContext?.showErrorMessage('Please connect an email account in settings to send invites.');
      } else {
        messagingContext?.showErrorMessage(
          messagingContext.messageFromError(error) || 'Failed to invite creator. Please try again.',
        );
      }
      setIsInviteToCampaignConfirmOpen(false);
    },
  });

  const [addToProgram, { loading: addToProgramLoading }] = useProspectsAddToProgram({
    onCompleted: () => {
      messagingContext?.showSuccessMessage(`Successfully added @${creator?.username} to the campaign!`);
      setIsAddToCampaignConfirmOpen(false);
    },
    onError: (error) => {
      console.error('Failed to add to campaign:', error);
      messagingContext?.showErrorMessage(
        messagingContext.messageFromError(error) || 'Failed to add creator to campaign. Please try again.',
      );
      setIsAddToCampaignConfirmOpen(false);
    },
  });

  const [addToCommunity, { loading: addToCommunityLoading }] = useProspectsAddToCommunity({
    onCompleted: () => {
      messagingContext?.showSuccessMessage(`Successfully added @${creator?.username} to the group!`);
      setIsAddToGroupConfirmOpen(false);
    },
    onError: (error) => {
      console.error('Failed to add to group:', error);
      messagingContext?.showErrorMessage(
        messagingContext.messageFromError(error) || 'Failed to add creator to group. Please try again.',
      );
      setIsAddToGroupConfirmOpen(false);
    },
  });

  // Action handlers
  const handleInviteToCampaign = (program: GetAllPrograms_programs) => {
    if (!creator || !messagingContext) return;

    if (program.status !== 'active') {
      messagingContext.showErrorMessage('Cannot invite to a campaign that is not active.');
      return;
    }

    if (!program.published) {
      messagingContext.showErrorMessage('Cannot invite to a campaign that has not been published.');
      return;
    }

    if (!canBeInvited) {
      const message = igdmActive
        ? 'Creator has no email address and is not in Instagram Creator Marketplace.'
        : 'Creator has no email address.';
      messagingContext.showInfoMessage(message);
      return;
    }

    setPendingInviteToCampaign(program);
    setIsInviteToCampaignConfirmOpen(true);
  };

  const handleAddToGroup = (group: GetCommunitiesQuery_communities) => {
    if (!creator) return;
    setPendingAddToGroup(group);
    setIsAddToGroupConfirmOpen(true);
  };

  const handleAddToCampaign = (program: GetAllPrograms_programs) => {
    if (!creator) return;
    setPendingAddToCampaign(program);
    setIsAddToCampaignConfirmOpen(true);
  };

  // Confirmation handlers
  const proceedWithInviteToCampaign = async () => {
    if (!pendingInviteToCampaign || !creator) {
      setIsInviteToCampaignConfirmOpen(false);
      return;
    }

    const prospectsForApi = transformCreatorsToProspects([creator]);

    try {
      await inviteToProgram({
        variables: {
          prospects: prospectsForApi,
          programIds: [pendingInviteToCampaign.id],
          resourceId: emailResource?.id,
          source: MUTATION_SOURCE,
        },
      });
    } catch (e) {
      console.error('Error during campaign invite process:', e);
      messagingContext?.showErrorMessage('An unexpected error occurred. Please try again.');
      setIsInviteToCampaignConfirmOpen(false);
    }
  };

  const proceedWithAddToGroup = async () => {
    if (!pendingAddToGroup || !creator) {
      setIsAddToGroupConfirmOpen(false);
      return;
    }

    const prospectsForApi = transformCreatorsToProspects([creator]);

    try {
      await addToCommunity({
        variables: {
          prospects: prospectsForApi,
          communityIds: [pendingAddToGroup.id],
          source: MUTATION_SOURCE,
        },
      });
    } catch (e) {
      console.error('Error during add to group process:', e);
      messagingContext?.showErrorMessage('An unexpected error occurred. Please try again.');
      setIsAddToGroupConfirmOpen(false);
    }
  };

  const proceedWithAddToCampaign = async () => {
    if (!pendingAddToCampaign || !creator) {
      setIsAddToCampaignConfirmOpen(false);
      return;
    }

    const prospectsForApi = transformCreatorsToProspects([creator]);

    try {
      await addToProgram({
        variables: {
          prospects: prospectsForApi,
          programIds: [pendingAddToCampaign.id],
          source: MUTATION_SOURCE,
        },
      });
    } catch (e) {
      console.error('Error during add to campaign process:', e);
      messagingContext?.showErrorMessage('An unexpected error occurred. Please try again.');
      setIsAddToCampaignConfirmOpen(false);
    }
  };

  return {
    // Menu props
    filteredCampaigns,
    filteredGroups,
    campaignSearchTerm,
    setCampaignSearchTerm,
    groupSearchTerm,
    setGroupSearchTerm,
    canBeInvited,

    // Action handlers
    onInviteToCampaign: handleInviteToCampaign,
    onAddToGroup: handleAddToGroup,
    onAddToCampaign: handleAddToCampaign,

    // Confirmation dialog props
    isInviteToCampaignConfirmOpen,
    pendingInviteToCampaign,
    proceedWithInviteToCampaign,
    closeInviteToCampaignConfirmDialog: () => setIsInviteToCampaignConfirmOpen(false),

    isAddToGroupConfirmOpen,
    pendingAddToGroup,
    proceedWithAddToGroup,
    closeAddToGroupConfirmDialog: () => setIsAddToGroupConfirmOpen(false),

    isAddToCampaignConfirmOpen,
    pendingAddToCampaign,
    proceedWithAddToCampaign,
    closeAddToCampaignConfirmDialog: () => setIsAddToCampaignConfirmOpen(false),

    // Loading states
    inviteLoading,
    addToCommunityLoading,
    addToProgramLoading,
  };
};
